import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Badge, Card, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AccountsReceivableModal from '../processing-order/modal/AccountsReceivableModal';
import StatementPdfPreviewModal from './modal/StatementPdfPreviewModal';
import OverdueStatisticCards from './components/OverdueStatisticCards';
import OverdueLoadingState from './components/OverdueLoadingState';
import OverdueListSearch from './components/OverdueListSearch';
import { CHART_BUCKETS_RECENT_START_INDEX, CHART_LABELS, CHART_LABELS_MOBILE, statementBucketIndexFromOverdueDay } from './overdueChartConstants';
import { socket } from '../../../socket';
import { rgbaColor } from '../../../utils/colorUtils';
import FalconCardHeader from '../../../components/common/FalconCardHeader';
import AdvanceTableProvider, { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import AdvanceTableFooter from '../../../components/common/advance-table/AdvanceTableFooter';
import CardDropdown from '../../../components/common/CardDropdown';
import { useAppContext } from '../../../providers/AppProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { useGetOverdueStatDataQuery, useLazyGetOverdueStatBucketQuery, useLazyGetOverdueStatDataQuery } from '../../../slices/dashboardApiSlice';
import { OVERDUE_CHART_RANGE_MODE } from '../../statistics/charts/overdue/lib/overdueChartConfig';
import {
	buildOverdueSummaryStats,
	countUniqueOverdueOrdersByPaymentType,
	OVERDUE_STAT_BUCKET_KEYS,
	sumArAmountByPaymentType,
} from '../../statistics/charts/overdue/lib/overdueStatChartData';
import { canAccessStatisticsOverdueData } from '../../statistics/common/lib/statisticsAccess';
import { hasPermission, PERM } from '../../../utils/accessPermissions';
import { matchPendingTotalPrice } from '../processing-order/common/pendingOrderKeywordMatch';

const EMPTY_OVERDUE_DATA = Object.freeze({});

const ALL_OVERDUE_BUCKET_KEYS = [...OVERDUE_STAT_BUCKET_KEYS.delivery, ...OVERDUE_STAT_BUCKET_KEYS.accept];

const overdueRowKey = (order) => order?.ar_id?.toString() || `${order?.order_id}_${order?.scheduleNode_id}_${order?.title}`;

const dedupeOverdueOrders = (list) => {
	const orders = Array.isArray(list) ? list : [];
	return orders.filter((order, index, self) => index === self.findIndex((item) => overdueRowKey(item) === overdueRowKey(order)));
};

const sumAccountsReceivable = (rows) =>
	(Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + (Number(row?.accountsReceivable) || 0), 0);

const countUniqueOrderIds = (rows) => {
	const ids = new Set();
	for (const row of Array.isArray(rows) ? rows : []) {
		const id = row?.order_id?.toString() || row?.orderId?.toString();
		if (id) ids.add(id);
	}
	return ids.size;
};

const matchOverdueOrderKeyword = (row, keyword) => {
	const q = String(keyword ?? '')
		.trim()
		.toLowerCase();
	if (!q) return true;
	if (!row || typeof row !== 'object') return false;

	/** 与占位一致：订单号 / 客户 / 应收标题；不含销售名，避免搜「测试用户」带出客户「测试客户」 */
	const haystack = [row.orderId, row.customerName, row.title]
		.filter(Boolean)
		.map((v) => String(v).toLowerCase());
	if (haystack.some((text) => text.includes(q))) return true;

	const raw = String(keyword ?? '').trim();
	return matchPendingTotalPrice(row.accountsReceivable, raw);
};

/** 搜索词变化时回到第 1 页 */
const OverdueKeywordPageReset = ({ keyword }) => {
	const { setPageIndex } = useAdvanceTableContext();
	const prevKeywordRef = useRef(keyword);
	useEffect(() => {
		if (prevKeywordRef.current === keyword) return;
		prevKeywordRef.current = keyword;
		try {
			setPageIndex(0);
		} catch {
			// ignore
		}
	}, [keyword, setPageIndex]);
	return null;
};

const Overdue = () => {
	const { breakpoints } = useBreakpoints();
	const isMobile = breakpoints.down('md');
	const { getThemeColor } = useAppContext();
	const { userInfo } = useSelector((state) => state.auth);
	const canFetchOverdue = canAccessStatisticsOverdueData(userInfo);
	const canExportStatement = hasPermission(userInfo, PERM.DASHBOARD_STATEMENT_PDF_EXPORT);
	const {
		data,
		isLoading,
		refetch: refetchOverdueStatistics,
	} = useGetOverdueStatDataQuery({ summaryOnly: true }, { skip: !canFetchOverdue });
	const overdueData = data ?? EMPTY_OVERDUE_DATA;
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const [orders, setOrders] = useState([]);
	/** 默认：超期 30 天以上 × 发货/提供服务（发货尾款） */
	const [showDatasets, setShowDatasets] = useState(0);
	const [showTableDataByPoint, setShowTableDataByPoint] = useState(0);
	const { setCurrentOrderOIdState } = useOrderContext();
	const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-bs-theme') || 'light');
	const chartRef = useRef(null);
	const [statementPdfModalOpen, setStatementPdfModalOpen] = useState(false);
	const [statementPdfSeedOrder, setStatementPdfSeedOrder] = useState(null);
	const [statementPdfSnapshotChartIndex, setStatementPdfSnapshotChartIndex] = useState(null);
	const [bucketOrderids, setBucketOrderids] = useState({});
	const [isBucketLoading, setIsBucketLoading] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [listKeyword, setListKeyword] = useState('');
	const [fetchOverdueBucket] = useLazyGetOverdueStatBucketQuery();
	const [fetchOverdueFull] = useLazyGetOverdueStatDataQuery();
	const hasListKeyword = Boolean(String(listKeyword).trim());

	useEffect(() => {
		if (!canFetchOverdue) {
			return undefined;
		}
		if (!socket.connected) {
			socket.connect();
		}
		const handleRefreshStats = (payload) => {
			if (payload?.scope === 'finalCostVerification' || payload?.scope === 'processingOnly') return;
			refetchOverdueStatistics();
		};
		socket.on('refresh dashboard stats', handleRefreshStats);
		return () => {
			socket.off('refresh dashboard stats', handleRefreshStats);
		};
	}, [canFetchOverdue, refetchOverdueStatistics]);

	const {
		totalDeliveryOverdue_30_orders,
		totalDeliveryOverdue_10_30_orders,
		totalDeliveryOverdue_0_10_orders,
		totalDelivery_0_10_orders,
		totalDelivery_10_30_orders,
		totalDelivery_30_60_orders,
		totalDelivery_60_90_orders,
		totalDelivery_90_180_orders,
		totalDelivery_180_360_orders,
		totalDelivery_360_plus_orders,
		totalAcceptOverdue_30_orders,
		totalAcceptOverdue_10_30_orders,
		totalAcceptOverdue_0_10_orders,
		totalAccept_0_10_orders,
		totalAccept_10_30_orders,
		totalAccept_30_60_orders,
		totalAccept_60_90_orders,
		totalAccept_90_180_orders,
		totalAccept_180_360_orders,
		totalAccept_360_plus_orders,
	} = overdueData;

	// 提取系统文字颜色
	const systemTextColor = useMemo(() => {
		const isDark = theme === 'dark';
		return isDark ? getThemeColor('white') : getThemeColor('gray-900');
	}, [theme, getThemeColor]);

	// 提取数据数组
	const deliveryData = useMemo(
		() => [
			totalDeliveryOverdue_30_orders?.totalAR || 0,
			totalDeliveryOverdue_10_30_orders?.totalAR || 0,
			totalDeliveryOverdue_0_10_orders?.totalAR || 0,
			totalDelivery_0_10_orders?.totalAR || 0,
			totalDelivery_10_30_orders?.totalAR || 0,
			totalDelivery_30_60_orders?.totalAR || 0,
			totalDelivery_60_90_orders?.totalAR || 0,
			totalDelivery_90_180_orders?.totalAR || 0,
			totalDelivery_180_360_orders?.totalAR || 0,
			totalDelivery_360_plus_orders?.totalAR || 0,
		],
		[
			totalDeliveryOverdue_30_orders?.totalAR,
			totalDeliveryOverdue_10_30_orders?.totalAR,
			totalDeliveryOverdue_0_10_orders?.totalAR,
			totalDelivery_0_10_orders?.totalAR,
			totalDelivery_10_30_orders?.totalAR,
			totalDelivery_30_60_orders?.totalAR,
			totalDelivery_60_90_orders?.totalAR,
			totalDelivery_90_180_orders?.totalAR,
			totalDelivery_180_360_orders?.totalAR,
			totalDelivery_360_plus_orders?.totalAR,
		],
	);

	const acceptData = useMemo(
		() => [
			totalAcceptOverdue_30_orders?.totalAR || 0,
			totalAcceptOverdue_10_30_orders?.totalAR || 0,
			totalAcceptOverdue_0_10_orders?.totalAR || 0,
			totalAccept_0_10_orders?.totalAR || 0,
			totalAccept_10_30_orders?.totalAR || 0,
			totalAccept_30_60_orders?.totalAR || 0,
			totalAccept_60_90_orders?.totalAR || 0,
			totalAccept_90_180_orders?.totalAR || 0,
			totalAccept_180_360_orders?.totalAR || 0,
			totalAccept_360_plus_orders?.totalAR || 0,
		],
		[
			totalAcceptOverdue_30_orders?.totalAR,
			totalAcceptOverdue_10_30_orders?.totalAR,
			totalAcceptOverdue_0_10_orders?.totalAR,
			totalAccept_0_10_orders?.totalAR,
			totalAccept_10_30_orders?.totalAR,
			totalAccept_30_60_orders?.totalAR,
			totalAccept_60_90_orders?.totalAR,
			totalAccept_90_180_orders?.totalAR,
			totalAccept_180_360_orders?.totalAR,
			totalAccept_360_plus_orders?.totalAR,
		],
	);

	// 订单数据映射表（汇总模式点柱后写入 bucketOrderids）
	const resolveBucketOrderids = useCallback(
		(paymentType, index) => {
			const key = OVERDUE_STAT_BUCKET_KEYS[paymentType]?.[index];
			if (!key) return [];
			const fromBucketCache = bucketOrderids[key];
			if (Array.isArray(fromBucketCache)) return fromBucketCache;
			const fromSummary = overdueData?.[key]?.orderids;
			return Array.isArray(fromSummary) ? fromSummary : [];
		},
		[bucketOrderids, overdueData],
	);

	const ordersMap = useMemo(
		() => ({
			0: Object.fromEntries(OVERDUE_STAT_BUCKET_KEYS.delivery.map((_, index) => [index, resolveBucketOrderids('delivery', index)])),
			1: Object.fromEntries(OVERDUE_STAT_BUCKET_KEYS.accept.map((_, index) => [index, resolveBucketOrderids('accept', index)])),
		}),
		[resolveBucketOrderids],
	);

	useEffect(() => {
		if (showDatasets === null || showTableDataByPoint === null) {
			setOrders((prev) => (prev.length === 0 ? prev : []));
			return;
		}

		const paymentType = showDatasets === 0 ? 'delivery' : 'accept';
		const bucketKey = OVERDUE_STAT_BUCKET_KEYS[paymentType]?.[showTableDataByPoint];
		if (!bucketKey) {
			setOrders((prev) => (prev.length === 0 ? prev : []));
			return;
		}

		const rawFromMap = resolveBucketOrderids(paymentType, showTableDataByPoint);
		const hasDetails = Array.isArray(rawFromMap) && rawFromMap.length > 0;
		const expectedCount = Number(overdueData?.[bucketKey]?.orderCount) || 0;
		const needsFetch = !hasDetails && expectedCount > 0 && !bucketOrderids[bucketKey];

		if (needsFetch) {
			let cancelled = false;
			setIsBucketLoading(true);
			fetchOverdueBucket(bucketKey)
				.unwrap()
				.then((payload) => {
					if (cancelled) return;
					const list = Array.isArray(payload?.[bucketKey]?.orderids) ? payload[bucketKey].orderids : [];
					const uniqueOrders = dedupeOverdueOrders(list);
					// 同一次更新写入 cache + 列表，避免「全卡先出、列表后到」
					setBucketOrderids((prev) => ({ ...prev, [bucketKey]: list }));
					setOrders(uniqueOrders);
				})
				.catch(() => {
					if (cancelled) return;
					setBucketOrderids((prev) => ({ ...prev, [bucketKey]: [] }));
					setOrders([]);
				})
				.finally(() => {
					if (!cancelled) setIsBucketLoading(false);
				});
			return () => {
				cancelled = true;
			};
		}

		const uniqueOrders = dedupeOverdueOrders(rawFromMap);
		setOrders(uniqueOrders);
		return undefined;
	}, [showDatasets, showTableDataByPoint, resolveBucketOrderids, overdueData, bucketOrderids, fetchOverdueBucket]);

	const needsAllDetailsForSearch = useMemo(() => {
		if (!hasListKeyword) return false;
		return ALL_OVERDUE_BUCKET_KEYS.some((key) => {
			const expectedCount = Number(overdueData?.[key]?.orderCount) || 0;
			if (expectedCount <= 0) return false;
			if (Array.isArray(bucketOrderids[key])) return false;
			const fromSummary = overdueData?.[key]?.orderids;
			return !(Array.isArray(fromSummary) && fromSummary.length > 0);
		});
	}, [hasListKeyword, overdueData, bucketOrderids]);

	useEffect(() => {
		if (!hasListKeyword || !needsAllDetailsForSearch) return undefined;
		let cancelled = false;
		setIsSearchLoading(true);
		fetchOverdueFull()
			.unwrap()
			.then((payload) => {
				if (cancelled) return;
				const next = {};
				for (const key of ALL_OVERDUE_BUCKET_KEYS) {
					const list = payload?.[key]?.orderids;
					next[key] = Array.isArray(list) ? list : [];
				}
				setBucketOrderids((prev) => ({ ...prev, ...next }));
			})
			.catch(() => {
				if (cancelled) return;
				setBucketOrderids((prev) => {
					const merged = { ...prev };
					for (const key of ALL_OVERDUE_BUCKET_KEYS) {
						if (!Array.isArray(merged[key])) merged[key] = [];
					}
					return merged;
				});
			})
			.finally(() => {
				if (!cancelled) setIsSearchLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// bucketOrderids / overdueData 仅用于 catch 兜底，不作为触发依赖
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在需要拉全量时触发
	}, [hasListKeyword, needsAllDetailsForSearch, fetchOverdueFull]);

	/** 搜索命中后按区间重算柱高与汇总；同一应收只计入先出现的区间，与列表合计一致 */
	const searchFilteredBuckets = useMemo(() => {
		if (!hasListKeyword || needsAllDetailsForSearch) return null;
		const seen = new Set();
		const buckets = {
			delivery: OVERDUE_STAT_BUCKET_KEYS.delivery.map(() => []),
			accept: OVERDUE_STAT_BUCKET_KEYS.accept.map(() => []),
		};
		for (const paymentType of ['delivery', 'accept']) {
			OVERDUE_STAT_BUCKET_KEYS[paymentType].forEach((_, index) => {
				for (const row of dedupeOverdueOrders(resolveBucketOrderids(paymentType, index))) {
					if (!matchOverdueOrderKeyword(row, listKeyword)) continue;
					const key = overdueRowKey(row);
					if (seen.has(key)) continue;
					seen.add(key);
					buckets[paymentType][index].push(row);
				}
			});
		}
		return buckets;
	}, [hasListKeyword, needsAllDetailsForSearch, listKeyword, resolveBucketOrderids]);

	useEffect(() => {
		if (overdueData === EMPTY_OVERDUE_DATA) return;
		setBucketOrderids({});
	}, [overdueData]);

	// 监听主题变化
	useEffect(() => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'data-bs-theme') {
					const newTheme = document.documentElement.getAttribute('data-bs-theme') || 'light';
					setTheme(newTheme);
				}
			});
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-bs-theme'],
		});

		return () => observer.disconnect();
	}, []);

	const formatCurrency = useCallback((amount) => {
		return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
	}, []);

	const formatChartAxisAmount = useCallback((value) => {
		const n = Number(value);
		if (!Number.isFinite(n)) {
			return '';
		}
		if (Math.abs(n) >= 10000) {
			return `${(n / 10000).toFixed(Math.abs(n) >= 100000 ? 0 : 1)}万`;
		}
		if (Math.abs(n) >= 1000) {
			return `${Math.round(n / 1000)}千`;
		}
		return String(n);
	}, []);

	// 生成颜色数组
	const generateSeverityColors = useCallback(() => {
		const primaryColor = getThemeColor('primary');
		const dangerColor = getThemeColor('danger');
		const warningColor = getThemeColor('warning');
		const successColor = getThemeColor('success');

		return {
			backgroundColors: [
				rgbaColor('#C41E3A', 0.8),
				rgbaColor(dangerColor, 0.75),
				rgbaColor('#FF6B35', 0.7),
				rgbaColor(warningColor, 0.65),
				rgbaColor(primaryColor, 0.65),
				rgbaColor('#4A90E2', 0.6),
				rgbaColor('#5BC0DE', 0.58),
				rgbaColor('#6BB6FF', 0.55),
				rgbaColor('#7ED321', 0.5),
				rgbaColor(successColor, 0.45),
			],
			borderColors: ['#C41E3A', dangerColor, '#FF6B35', warningColor, primaryColor, '#4A90E2', '#5BC0DE', '#6BB6FF', '#7ED321', successColor],
		};
	}, [getThemeColor]); // 添加 theme 依赖，确保主题切换时颜色更新

	// 缓存颜色数组
	const severityColors = useMemo(() => generateSeverityColors(), [generateSeverityColors]);

	// 根据逾期天数获取对应的颜色
	const getColorByOverdueDay = useCallback(
		(overdueDay) => {
			const borderColors = severityColors.borderColors;
			if (overdueDay < -30) {
				return borderColors[0];
			} else if (overdueDay >= -30 && overdueDay < -10) {
				return borderColors[1];
			} else if (overdueDay >= -10 && overdueDay < 0) {
				return borderColors[2];
			} else if (overdueDay >= 0 && overdueDay <= 10) {
				return borderColors[3];
			} else if (overdueDay > 10 && overdueDay <= 30) {
				return borderColors[4];
			} else if (overdueDay > 30 && overdueDay <= 60) {
				return borderColors[5];
			} else if (overdueDay > 60 && overdueDay <= 90) {
				return borderColors[6];
			} else if (overdueDay > 90 && overdueDay <= 180) {
				return borderColors[7];
			} else if (overdueDay > 180 && overdueDay <= 360) {
				return borderColors[8];
			} else {
				return borderColors[9];
			}
		},
		[severityColors],
	);

	// 通用数据集配置
	const commonDatasetConfig = useMemo(
		() => ({
			borderWidth: isMobile ? 1 : 2,
			borderRadius: isMobile ? 4 : 6,
			borderSkipped: false,
			...(isMobile
				? {
						barThickness: 'flex',
						maxBarThickness: 13,
						categoryPercentage: 0.78,
						barPercentage: 0.52,
					}
				: {
						barThickness: 'flex',
						maxBarThickness: 50,
					}),
		}),
		[isMobile],
	);

	const chartDeliveryData = useMemo(
		() => (searchFilteredBuckets ? searchFilteredBuckets.delivery.map((rows) => sumAccountsReceivable(rows)) : deliveryData),
		[searchFilteredBuckets, deliveryData],
	);
	const chartAcceptData = useMemo(
		() => (searchFilteredBuckets ? searchFilteredBuckets.accept.map((rows) => sumAccountsReceivable(rows)) : acceptData),
		[searchFilteredBuckets, acceptData],
	);

	const barChartData = useMemo(() => {
		return {
			labels: isMobile ? CHART_LABELS_MOBILE : CHART_LABELS,
			datasets: [
				{
					label: '发货尾款',
					data: chartDeliveryData,
					backgroundColor: severityColors.backgroundColors,
					borderColor: severityColors.borderColors,
					...commonDatasetConfig,
				},
				{
					label: '验收尾款',
					data: chartAcceptData,
					backgroundColor: severityColors.backgroundColors,
					borderColor: severityColors.borderColors,
					...commonDatasetConfig,
				},
			],
		};
	}, [severityColors, chartDeliveryData, chartAcceptData, commonDatasetConfig, isMobile]);

	const barChartOptions = useMemo(() => {
		const isDark = theme === 'dark';
		const gridColor = isDark ? rgbaColor(getThemeColor('gray-300'), 0.3) : getThemeColor('gray-200');
		const tickColor = isDark ? getThemeColor('gray-400') : getThemeColor('gray-600');
		const tickFontSize = isMobile ? 10 : 11;

		return {
			indexAxis: isMobile ? 'y' : 'x',
			responsive: true,
			maintainAspectRatio: false,
			...(isMobile
				? {
						datasets: {
							bar: {
								categoryPercentage: 0.78,
								barPercentage: 0.52,
							},
						},
					}
				: {}),
			layout: isMobile ? { padding: { left: 0, right: 4 } } : undefined,
			animation: {
				duration: isMobile ? 600 : 1000,
				easing: 'easeInOutQuart',
			},
			interaction: isMobile
				? {
						/* 横排双柱：index 模式会同时命中发货+验收，onClick 若只取 [0] 会永远选发货 */
						mode: 'nearest',
						axis: 'x',
						intersect: true,
					}
				: {
						mode: 'nearest',
						axis: 'y',
						intersect: true,
					},
			plugins: {
				datalabels: {
					anchor: 'end',
					align: 'top',
					offset: 4,
					color: systemTextColor,
					font: {
						size: isMobile ? 8 : 10,
						weight: '500',
					},
					formatter: function (value) {
						if (value === 0 || value === null || value === undefined) {
							return '';
						}
						return formatCurrency(value);
					},
					display: function (context) {
						if (isMobile) {
							return false;
						}
						return context.dataset.data[context.dataIndex] > 0;
					},
				},
				tooltip: {
					enabled: true,
					backgroundColor: isDark ? getThemeColor('dark') : getThemeColor('white'),
					titleColor: isDark ? getThemeColor('white') : getThemeColor('gray-900'),
					bodyColor: isDark ? getThemeColor('white') : getThemeColor('gray-700'),
					borderColor: isDark ? getThemeColor('gray-600') : getThemeColor('gray-300'),
					borderWidth: 1,
					padding: 12,
					displayColors: true,
					boxPadding: 6,
					titleFont: {
						size: 13,
						weight: 'bold',
					},
					bodyFont: {
						size: 12,
					},
					callbacks: {
						title: function (context) {
							const dataIndex = context[0].dataIndex;
							const labels = isMobile ? CHART_LABELS_MOBILE : CHART_LABELS;
							return labels[dataIndex] || '';
						},
						label: function (context) {
							const datasetLabel = context.dataset.label || '';
							const value = (isMobile ? context.parsed.x : context.parsed.y) || 0;
							const datasetIndex = context.datasetIndex;
							const dataIndex = context.dataIndex;
							const paymentType = datasetIndex === 0 ? 'delivery' : 'accept';
							const orders = searchFilteredBuckets
								? searchFilteredBuckets[paymentType]?.[dataIndex] || []
								: ordersMap[datasetIndex]?.[dataIndex] || [];

							const lines = [];
							lines.push(`${datasetLabel}: ${formatCurrency(value)}`);
							if (orders.length > 0) {
								lines.push(`订单数量: ${orders.length} 个`);
							}
							return lines;
						},
						labelColor: function (context) {
							const borderColor = Array.isArray(context.dataset.borderColor) ? context.dataset.borderColor[context.dataIndex] : context.dataset.borderColor;
							return {
								borderColor: borderColor,
								backgroundColor: borderColor,
							};
						},
					},
				},
				legend: {
					display: false,
				},
			},
			scales: isMobile
				? {
						x: {
							beginAtZero: true,
							grid: {
								display: true,
								color: gridColor,
								drawBorder: false,
								drawOnChartArea: true,
								drawTicks: false,
							},
							ticks: {
								color: tickColor,
								maxTicksLimit: 5,
								font: {
									size: tickFontSize,
									weight: '500',
								},
								padding: 4,
								callback: formatChartAxisAmount,
							},
							border: {
								display: false,
							},
						},
						y: {
							grid: {
								display: false,
							},
							ticks: {
								color: tickColor,
								autoSkip: false,
								font: {
									size: tickFontSize,
									weight: '500',
								},
								padding: 2,
							},
							border: {
								display: false,
							},
						},
					}
				: {
						x: {
							stacked: false,
							grid: {
								display: true,
								color: gridColor,
								drawBorder: false,
								drawOnChartArea: true,
								drawTicks: false,
							},
							ticks: {
								color: tickColor,
								maxRotation: 0,
								minRotation: 0,
								font: {
									size: tickFontSize,
									weight: '500',
								},
								padding: 8,
							},
							border: {
								display: false,
							},
						},
						y: {
							stacked: false,
							beginAtZero: true,
							grid: {
								display: true,
								color: gridColor,
								drawBorder: false,
								drawOnChartArea: true,
								drawTicks: false,
							},
							ticks: {
								color: tickColor,
								font: {
									size: tickFontSize,
									weight: '500',
								},
								padding: 8,
							},
							border: {
								display: false,
							},
						},
					},
			onClick: (event, barElement, chart) => {
				const chartInstance = chart ?? chartRef.current;
				let hits = barElement;

				if ((!hits || hits.length === 0) && chartInstance) {
					hits = chartInstance.getElementsAtEventForMode(event.native ?? event, 'nearest', { axis: isMobile ? 'x' : 'y', intersect: true }, false);
				}
				if (!hits?.length) return;

				let target = hits[0];
				if (isMobile && hits.length > 1 && chartInstance?.canvas) {
					const nativeEvent = event.native ?? event;
					const rect = chartInstance.canvas.getBoundingClientRect();
					const scaleX = chartInstance.width / rect.width;
					const clickX = (nativeEvent.clientX - rect.left) * scaleX;
					target = hits.reduce(
						(best, el) => {
							const bar = chartInstance.getDatasetMeta(el.datasetIndex).data[el.index];
							if (!bar || typeof bar.x !== 'number' || typeof bar.base !== 'number') return best;
							const dist = Math.abs(clickX - (bar.x + bar.base) / 2);
							return dist < best.dist ? { el, dist } : best;
						},
						{ el: hits[0], dist: Infinity },
					).el;
				}

				setShowDatasets(target.datasetIndex);
				setShowTableDataByPoint(target.index);
			},
		};
	}, [getThemeColor, formatCurrency, formatChartAxisAmount, ordersMap, searchFilteredBuckets, theme, systemTextColor, isMobile]);

	const handleOrderClick = useCallback(
		(orderId) => {
			setCurrentOrderOIdState(orderId);
			setIsOpenPendingModal(true);
			socket.emit('join order', orderId);
		},
		[setCurrentOrderOIdState],
	);

	const openStatementPdfModalForOrder = useCallback((original) => {
		setStatementPdfSnapshotChartIndex(statementBucketIndexFromOverdueDay(original?.overdueDay));
		setStatementPdfSeedOrder(original);
		setStatementPdfModalOpen(true);
	}, []);

	const columns = useMemo(
		() => [
			{
				accessorKey: 'orderId',
				header: '订单编号',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: { className: 'fw-semibold text-start' },
				},
				cell: ({ row: { original } }) => {
					const { orderId, order_id, overdueDay } = original;
					const color = getColorByOverdueDay(overdueDay) || '#e63757';
					return (
						<Link
							onClick={(e) => {
								e.preventDefault();
								handleOrderClick(order_id);
							}}
							className='text-decoration-none hover-primary'
							style={{ color }}
							to='#'
						>
							{orderId || '-'}
						</Link>
					);
				},
			},
			{
				accessorKey: 'customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return <Link style={{ color }}>{original.customerName}</Link>;
				},
			},
			{
				accessorKey: 'saleUserName',
				header: '销售员',
				meta: {
					headerProps: { className: 'text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return <span style={{ color }}>{original.saleUserName || '—'}</span>;
				},
			},
			{
				accessorKey: 'assistantUserName',
				header: '销售助理',
				meta: {
					headerProps: { className: 'text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return <span style={{ color }}>{original.assistantUserName || '—'}</span>;
				},
			},
			{
				accessorKey: 'title',
				header: '应收账款',
				meta: {
					headerProps: { className: 'sticky-column text-900' },
					cellProps: { className: 'fw-semibold' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return <Link style={{ color }}>{original.title}</Link>;
				},
			},
			{
				accessorKey: 'accountsReceivable',
				header: '金额',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-end' },
					cellProps: { className: 'fw-semibold text-end' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return (
						<Badge
							pill
							className='fw-semibold'
							style={{
								backgroundColor: color,
								color: '#ffffff',
								border: 'none',
								'--bs-badge-bg': color,
							}}
						>
							<span className='fs-11' style={{ color: '#ffffff' }}>
								{formatCurrency(original.accountsReceivable)}
							</span>
						</Badge>
					);
				},
			},
			{
				accessorKey: 'overdueDay',
				header: '天数',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-end' },
					cellProps: { className: 'fw-semibold text-end' },
					card: { role: 'status' },
				},
				cell: ({ row: { original } }) => {
					const color = getColorByOverdueDay(original.overdueDay) || '#e63757';
					return (
						<Badge
							pill
							style={{
								backgroundColor: color,
								color: '#ffffff',
								border: 'none',
								'--bs-badge-bg': color,
							}}
						>
							<span className='fs-11' style={{ color: '#ffffff' }}>
								{original.overdueDay}天
							</span>
						</Badge>
					);
				},
			},
			{
				id: 'actions',
				header: '',
				meta: {
					headerProps: { className: 'text-end' },
					cellProps: { className: 'text-end' },
				},
				enableSorting: false,
				cell: ({ row: { original } }) => (
					<CardDropdown icon='ellipsis-h'>
						<div className='py-2'>
							<Dropdown.Item onClick={() => handleOrderClick(original.order_id)}>{`查看订单`}</Dropdown.Item>
							{canExportStatement ? (
							<Dropdown.Item onClick={() => openStatementPdfModalForOrder(original)} className='text-danger'>
								生成对账单 PDF
							</Dropdown.Item>
							) : null}
						</div>
					</CardDropdown>
				),
			},
		],
		[handleOrderClick, openStatementPdfModalForOrder, formatCurrency, getColorByOverdueDay, canExportStatement],
	);

	// 与经营看板/超期图共用 overdueStatChartData 口径，避免首页与统计页订单数漂移；搜索时改按命中应收重算
	const statistics = useMemo(() => {
		const deliveryRows = searchFilteredBuckets?.delivery.flat() ?? null;
		const acceptRows = searchFilteredBuckets?.accept.flat() ?? null;
		const overdueRows = searchFilteredBuckets
			? [
					...searchFilteredBuckets.delivery.slice(0, CHART_BUCKETS_RECENT_START_INDEX).flat(),
					...searchFilteredBuckets.accept.slice(0, CHART_BUCKETS_RECENT_START_INDEX).flat(),
				]
			: null;
		const allScopeStats = searchFilteredBuckets
			? null
			: buildOverdueSummaryStats(overdueData, { rangeMode: OVERDUE_CHART_RANGE_MODE.ALL });
		const overdueScopeStats = searchFilteredBuckets
			? null
			: buildOverdueSummaryStats(overdueData, { rangeMode: OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY });
		const deliveryTotal = searchFilteredBuckets ? sumAccountsReceivable(deliveryRows) : sumArAmountByPaymentType(overdueData, 'delivery');
		const acceptTotal = searchFilteredBuckets ? sumAccountsReceivable(acceptRows) : sumArAmountByPaymentType(overdueData, 'accept');
		const deliveryCount = searchFilteredBuckets
			? countUniqueOrderIds(deliveryRows)
			: countUniqueOverdueOrdersByPaymentType(overdueData, 'delivery', OVERDUE_CHART_RANGE_MODE.ALL);
		const acceptCount = searchFilteredBuckets
			? countUniqueOrderIds(acceptRows)
			: countUniqueOverdueOrdersByPaymentType(overdueData, 'accept', OVERDUE_CHART_RANGE_MODE.ALL);

		const currentOrders = showDatasets !== null && showTableDataByPoint !== null ? ordersMap[showDatasets]?.[showTableDataByPoint] || [] : [];
		const currentCount =
			currentOrders.length > 0
				? currentOrders.length
				: showDatasets !== null && showTableDataByPoint !== null
					? Number(
							overdueData?.[
								OVERDUE_STAT_BUCKET_KEYS[showDatasets === 0 ? 'delivery' : 'accept']?.[showTableDataByPoint]
							]?.orderCount,
						) || 0
					: 0;
		const currentTotal =
			currentOrders.length > 0
				? currentOrders.reduce((sum, order) => sum + (order.accountsReceivable || 0), 0)
				: showDatasets !== null && showTableDataByPoint !== null
					? Number(
							overdueData?.[
								OVERDUE_STAT_BUCKET_KEYS[showDatasets === 0 ? 'delivery' : 'accept']?.[showTableDataByPoint]
							]?.totalAR,
						) || 0
					: 0;

		return {
			deliveryTotal,
			acceptTotal,
			overdueTotal: searchFilteredBuckets ? sumAccountsReceivable(overdueRows) : allScopeStats.overdueTotal,
			total: searchFilteredBuckets ? deliveryTotal + acceptTotal : allScopeStats.salesTotal,
			deliveryCount,
			acceptCount,
			overdueCount: searchFilteredBuckets ? countUniqueOrderIds(overdueRows) : overdueScopeStats.orderCount,
			totalCount: searchFilteredBuckets ? countUniqueOrderIds([...(deliveryRows || []), ...(acceptRows || [])]) : allScopeStats.orderCount,
			currentCount,
			currentTotal,
		};
	}, [overdueData, showDatasets, showTableDataByPoint, ordersMap, searchFilteredBuckets]);

	// 统计卡片配置
	const statisticCards = useMemo(
		() => [
			{
				title: '总应收账款',
				value: statistics.total,
				count: statistics.totalCount,
				icon: 'sack-dollar',
				color: 'primary',
				bgGradient: 'linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.12) 0%, rgba(var(--app-theme-primary-rgb), 0.06) 100%)',
				hoverShadow: '0 8px 16px rgba(var(--app-theme-primary-rgb), 0.2)',
			},
			{
				title: '超期总额',
				value: statistics.overdueTotal,
				count: statistics.overdueCount,
				icon: 'triangle-exclamation',
				color: 'danger',
				bgGradient: 'linear-gradient(135deg, rgba(220, 53, 69, 0.12) 0%, rgba(220, 53, 69, 0.06) 100%)',
				hoverShadow: '0 8px 16px rgba(220, 53, 69, 0.2)',
			},
			{
				title: '发货尾款',
				value: statistics.deliveryTotal,
				count: statistics.deliveryCount,
				icon: 'truck',
				color: 'info',
				bgGradient: 'linear-gradient(135deg, rgba(13, 110, 253, 0.12) 0%, rgba(13, 110, 253, 0.06) 100%)',
				hoverShadow: '0 8px 16px rgba(13, 110, 253, 0.2)',
			},
			{
				title: '验收尾款',
				value: statistics.acceptTotal,
				count: statistics.acceptCount,
				icon: 'check-circle',
				color: 'success',
				bgGradient: 'linear-gradient(135deg, rgba(25, 135, 84, 0.12) 0%, rgba(25, 135, 84, 0.06) 100%)',
				hoverShadow: '0 8px 16px rgba(25, 135, 84, 0.2)',
			},
		],
		[statistics],
	);

	const filteredOrders = useMemo(() => {
		if (hasListKeyword) {
			if (!searchFilteredBuckets) return [];
			return [...searchFilteredBuckets.delivery.flat(), ...searchFilteredBuckets.accept.flat()];
		}
		return orders;
	}, [hasListKeyword, searchFilteredBuckets, orders]);

	const searchHitTotal = useMemo(() => {
		if (!hasListKeyword) return 0;
		return filteredOrders.reduce((sum, order) => sum + (Number(order.accountsReceivable) || 0), 0);
	}, [hasListKeyword, filteredOrders]);

	/** 当前选中区间明细尚未就绪（含 effect 置 isBucketLoading 前的一拍） */
	const awaitingCurrentBucketDetails = useMemo(() => {
		if (hasListKeyword || isLoading) return false;
		if (showDatasets === null || showTableDataByPoint === null) return false;
		const paymentType = showDatasets === 0 ? 'delivery' : 'accept';
		const bucketKey = OVERDUE_STAT_BUCKET_KEYS[paymentType]?.[showTableDataByPoint];
		if (!bucketKey) return false;
		const expectedCount = Number(overdueData?.[bucketKey]?.orderCount) || 0;
		if (expectedCount <= 0) return false;
		if (Array.isArray(bucketOrderids[bucketKey])) return false;
		const fromSummary = overdueData?.[bucketKey]?.orderids;
		if (Array.isArray(fromSummary) && fromSummary.length > 0) return orders.length === 0;
		return true;
	}, [hasListKeyword, isLoading, showDatasets, showTableDataByPoint, overdueData, bucketOrderids, orders.length]);

	const isChartLoading = isLoading;
	const isListLoading =
		isLoading || isBucketLoading || awaitingCurrentBucketDetails || (hasListKeyword && (isSearchLoading || needsAllDetailsForSearch));
	const showOrderList = hasListKeyword || orders.length > 0;

	const table = useAdvanceTable({
		data: filteredOrders,
		columns: columns,
		sortable: true,
		pagination: true,
		perPage: 7,
	});

	return (
		<>
			<AdvanceTableProvider {...table}>
				<Card className='dashboard-overdue-card'>
					<FalconCardHeader
						title={
							<div className='d-flex align-items-center gap-2 dashboard-overdue-card__title-wrap'>
								<FontAwesomeIcon icon='chart-bar' className='text-primary' />
								<span>超期金额统计</span>
							</div>
						}
						titleTag='h6'
						className='pb-0 dashboard-overdue-card__header'
					/>
					<Card.Body className='dashboard-overdue-card__body' style={{ maxHeight: 'none', overflow: 'visible' }}>
						{isChartLoading ? (
							<OverdueLoadingState mode='chart' />
						) : (
							<>
								<OverdueStatisticCards cards={statisticCards} formatCurrency={formatCurrency} />

								{isMobile ? (
									<p className='dashboard-overdue-chart-tip text-muted small mb-2 mb-0'>
										点击对应柱子查看订单；每行两根柱分别为发货尾款（上）与验收尾款（下）
									</p>
								) : null}
								<div
									className={classNames('dashboard-overdue-chart-wrap position-relative', {
										'dashboard-overdue-chart-wrap--horizontal': isMobile,
									})}
								>
									<div className='position-absolute top-0 end-0 d-flex gap-2 align-items-center mb-2 dashboard-overdue-chart-hint' style={{ zIndex: 10 }}>
										<OverlayTrigger
											placement='left'
											overlay={
												<Tooltip id='chart-tooltip'>
													<div className='text-start'>
														<div>点击图表中的柱状图可查看对应订单详情</div>
														<div className='text-muted small mt-1'>不同颜色代表不同的超期时间段</div>
													</div>
												</Tooltip>
											}
										>
											<FontAwesomeIcon icon='circle-question' className='text-muted cursor-pointer' style={{ fontSize: '0.875rem' }} />
										</OverlayTrigger>
									</div>
									<div className='dashboard-overdue-chart-inner'>
										<Bar ref={chartRef} data={barChartData} options={barChartOptions} key={`${theme}-${isMobile ? 'm' : 'd'}-${listKeyword}`} />
									</div>
								</div>
							</>
						)}

						{/* 状态横幅 + 右侧搜索 */}
						<div
							className='alert alert-info d-flex align-items-center gap-2 py-2 border-0 shadow-sm dashboard-overdue-filter-alert'
							role='status'
							style={{
								background: 'linear-gradient(135deg, rgba(13, 202, 240, 0.15) 0%, rgba(13, 202, 240, 0.08) 100%)',
								borderLeft: '4px solid #0dcaf0 !important',
							}}
						>
							<FontAwesomeIcon
								icon={hasListKeyword ? 'search' : 'info-circle'}
								className='text-info flex-shrink-0 dashboard-overdue-filter-alert__icon'
							/>
							<div className='flex-grow-1 min-w-0 small dashboard-overdue-filter-alert__body'>
								{hasListKeyword ? (
									<>
										<div className='dashboard-overdue-filter-alert__labels'>
											<span className='dashboard-overdue-filter-alert__prefix'>搜索全部区间:</span>
											<Badge bg='info' className='dashboard-overdue-filter-alert__badge'>
												{listKeyword}
											</Badge>
										</div>
										<div className='text-muted dashboard-overdue-filter-alert__meta'>
											{isListLoading ? '正在加载全部区间明细…' : `${filteredOrders.length} 条应收 · 合计 `}
											{!isListLoading ? <strong className='text-info'>{formatCurrency(searchHitTotal)}</strong> : null}
										</div>
									</>
								) : isChartLoading || isListLoading ? (
									<>
										<div className='dashboard-overdue-filter-alert__labels'>
											<span className='dashboard-overdue-filter-alert__prefix'>超期订单</span>
										</div>
										<div className='text-muted dashboard-overdue-filter-alert__meta'>
											{isChartLoading ? '正在加载图表…' : '正在加载区间明细…'}
										</div>
									</>
								) : showDatasets !== null && showTableDataByPoint !== null && statistics.currentCount > 0 ? (
									<>
										<div className='dashboard-overdue-filter-alert__labels'>
											<span className='dashboard-overdue-filter-alert__prefix'>当前显示:</span>
											<Badge bg='info' className='dashboard-overdue-filter-alert__badge'>
												{(isMobile ? CHART_LABELS_MOBILE : CHART_LABELS)[showTableDataByPoint]}
											</Badge>
											<Badge bg={showDatasets === 0 ? 'info' : 'success'} className='dashboard-overdue-filter-alert__badge'>
												{showDatasets === 0 ? '发货尾款' : '验收尾款'}
											</Badge>
										</div>
										<div className='text-muted dashboard-overdue-filter-alert__meta'>
											{statistics.currentCount} 个订单 · 合计 <strong className='text-info'>{formatCurrency(statistics.currentTotal)}</strong>
										</div>
									</>
								) : (
									<>
										<div className='dashboard-overdue-filter-alert__labels'>
											<span className='dashboard-overdue-filter-alert__prefix'>超期订单</span>
										</div>
										<div className='text-muted dashboard-overdue-filter-alert__meta'>点击柱状图查看区间明细，或右侧搜索全部区间</div>
									</>
								)}
							</div>
							<div className='dashboard-overdue-filter-alert__search flex-shrink-0'>
								<OverdueListSearch keyword={listKeyword} setKeyword={setListKeyword} />
							</div>
						</div>

						{isListLoading ? <OverdueLoadingState mode='rows' /> : null}
						{!isListLoading && showOrderList && (
							<>
								<OverdueKeywordPageReset keyword={listKeyword} />
								{filteredOrders.length > 0 ? (
									<AdvanceTableResponsive
										cardListClassName='dashboard-overdue-order-cards'
										headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
										rowClassName='btn-reveal-trigger text-nowrap align-middle'
										tableProps={{
											className: 'fs-10 fw-semibold overflow-hidden overdue-table',
										}}
									/>
								) : (
									<div className='text-center py-4'>
										<p className='text-muted mb-1 fw-semibold'>无匹配订单</p>
										<p className='text-muted small mb-0'>
											{hasListKeyword
												? `全部区间内未找到「${listKeyword}」，可清空搜索或换关键词`
												: '当前时段暂无对应订单'}
										</p>
									</div>
								)}
								{filteredOrders.length > 0 ? (
									<AdvanceTableFooter rowInfo navButtons className='px-2 px-md-3 dashboard-overdue-table-footer' />
								) : null}
							</>
						)}

						{!showOrderList && !isListLoading && (
							<div className='text-center py-5'>
								<div className='mb-3'>
									<FontAwesomeIcon icon='chart-line' className='text-muted' style={{ fontSize: '3rem', opacity: 0.5 }} />
								</div>
								<p className='text-muted mb-2 fw-semibold'>暂无订单数据</p>
								<p className='text-muted small mb-0'>
									{showDatasets !== null && showTableDataByPoint !== null ? (
										'当前时段暂无对应订单，可点击图表切换其他区间，或横幅右侧搜索全部区间'
									) : (
										<>
											<FontAwesomeIcon icon='hand-pointer' className='me-1' />
											点击图表中的柱状图查看对应订单详情，或横幅右侧搜索全部区间
										</>
									)}
								</p>
							</div>
						)}
					</Card.Body>
				</Card>
			</AdvanceTableProvider>
			<AccountsReceivableModal setIsOpenPendingModal={setIsOpenPendingModal} isOpenPendingModal={isOpenPendingModal} />
			<StatementPdfPreviewModal
				show={statementPdfModalOpen}
				onHide={() => {
					setStatementPdfModalOpen(false);
					setStatementPdfSeedOrder(null);
					setStatementPdfSnapshotChartIndex(null);
				}}
				seedOrder={statementPdfSeedOrder}
				snapshotChartIndex={statementPdfSnapshotChartIndex}
			/>
		</>
	);
};

export default Overdue;
